import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// ============================================================================
// Constants
// ============================================================================

const NEON = '#DCF995';
const MUTED = 'rgba(255,255,255,0.4)';
const TEXT_WHITE = '#ffffff';

const baseInputStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: TEXT_WHITE,
  fontSize: '22px',
  fontFamily: 'inherit',
  width: '100%',
  height: '100%',
  caretColor: NEON,
};

// ============================================================================
// Animations
// ============================================================================

const drawCheck = keyframes`
  from { stroke-dashoffset: 24; }
  to   { stroke-dashoffset: 0; }
`;

// ============================================================================
// Types
// ============================================================================

export type SmartInputMode = 'name' | 'text' | 'search' | 'password' | 'code' | 'tos';

export interface SmartInputProps {
  mode: SmartInputMode;
  value: string;
  onChange: (value: string) => void;
  secondaryValue?: string;
  onSecondaryChange?: (value: string) => void;
  suggestions?: string[];
  onSuggestionSelect?: (value: string) => void;
  checkmarks?: [boolean, boolean];
  placeholder?: string;
  secondaryPlaceholder?: string;
  inputType?: string;
  onSubmit: () => void;
}

// ============================================================================
// AnimatedCheckmark (internal)
// ============================================================================

const AnimatedCheckmark: React.FC<{ visible: boolean; delay?: number }> = ({ visible, delay = 0 }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.2s', transitionDelay: `${delay}ms` }}>
    <circle cx="14" cy="14" r="13" fill="none" stroke={NEON} strokeWidth="2" />
    <polyline
      points="8,15 12,19 20,10"
      fill="none"
      stroke={NEON}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        strokeDasharray: 24,
        strokeDashoffset: visible ? 0 : 24,
        animation: visible ? `${drawCheck} 0.4s ease-out ${delay}ms forwards` : 'none',
      }}
    />
  </svg>
);

// ============================================================================
// NameInput
// ============================================================================

const NameInput: React.FC<{
  firstName: string;
  onFirstNameChange: (v: string) => void;
  lastName: string;
  onLastNameChange: (v: string) => void;
  placeholder?: string;
  onSubmit: () => void;
}> = ({ firstName, onFirstNameChange, lastName, onLastNameChange, placeholder, onSubmit }) => {
  const [split, setSplit] = useState(false);
  const [activeSide, setActiveSide] = useState<'left' | 'right'>('left');
  const inputRef = useRef<HTMLInputElement>(null);
  const leftRef = useRef<HTMLInputElement>(null);
  const rightRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (split) {
        if (activeSide === 'right') rightRef.current?.focus();
        else leftRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [split, activeSide]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, side: 'left' | 'right') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (side === 'left' && !split) {
        if (firstName.trim() && lastName.trim()) onSubmit();
        return;
      }
      if (side === 'left') {
        setActiveSide('right');
        rightRef.current?.focus();
      } else {
        onSubmit();
      }
      return;
    }
    if (e.key === ' ' && side === 'left' && !split && firstName.trim().length > 0) {
      e.preventDefault();
      setSplit(true);
      setActiveSide('right');
    }
  }, [split, firstName, lastName, onSubmit]);

  if (!split) {
    return (
      <input
        ref={inputRef}
        style={{ ...baseInputStyle, padding: '0 24px' }}
        value={firstName}
        onChange={e => onFirstNameChange(e.target.value)}
        onKeyDown={e => handleKeyDown(e, 'left')}
        placeholder={placeholder || 'Your name'}
        autoFocus
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center' }}>
      <Box
        sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 3, py: 1.5, cursor: 'text', position: 'relative' }}
        onClick={() => { setActiveSide('left'); leftRef.current?.focus(); }}
      >
        <Typography sx={{ fontSize: '11px', color: MUTED, mb: 0.5, letterSpacing: '0.5px', textTransform: 'uppercase' }}>First Name</Typography>
        <input
          ref={leftRef}
          style={{ ...baseInputStyle, padding: 0, fontSize: '20px' }}
          value={firstName}
          onChange={e => onFirstNameChange(e.target.value)}
          onKeyDown={e => handleKeyDown(e, 'left')}
          onFocus={() => setActiveSide('left')}
        />
      </Box>
      <Box sx={{ width: '1px', height: '60%', bgcolor: 'rgba(255,255,255,0.15)' }} />
      <Box
        sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 3, py: 1.5, cursor: 'text', position: 'relative' }}
        onClick={() => { setActiveSide('right'); rightRef.current?.focus(); }}
      >
        <Typography sx={{ fontSize: '11px', color: MUTED, mb: 0.5, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Last Name</Typography>
        <input
          ref={rightRef}
          style={{ ...baseInputStyle, padding: 0, fontSize: '20px' }}
          value={lastName}
          onChange={e => onLastNameChange(e.target.value)}
          onKeyDown={e => handleKeyDown(e, 'right')}
          onFocus={() => setActiveSide('right')}
          autoFocus
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// SearchInput
// ============================================================================

const SearchInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  onSuggestionSelect: (v: string) => void;
  placeholder?: string;
  onSubmit: () => void;
}> = ({ value, onChange, suggestions, onSuggestionSelect, placeholder, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions.length, value]);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        onSuggestionSelect(suggestions[selectedIndex]);
      } else {
        onSuggestionSelect(value);
      }
      setTimeout(() => onSubmit(), 50);
    }
  }, [suggestions, selectedIndex, value, onSuggestionSelect, onSubmit]);

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', px: 3 }}>
      <input
        ref={inputRef}
        style={{ ...baseInputStyle, padding: 0, flex: 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus
      />
      {suggestions.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexShrink: 0,
            ml: 2,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            maxWidth: { xs: '45%', sm: 'none' },
            maskImage: { xs: 'linear-gradient(to right, black 60%, transparent 100%)', sm: 'none' },
            WebkitMaskImage: { xs: 'linear-gradient(to right, black 60%, transparent 100%)', sm: 'none' },
          }}
        >
          {suggestions.map((s, i) => (
            <Box
              key={s}
              onClick={() => {
                onSuggestionSelect(s);
                setTimeout(() => onSubmit(), 50);
              }}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                fontSize: '13px',
                color: i === selectedIndex ? NEON : 'rgba(255,255,255,0.5)',
                bgcolor: i === selectedIndex ? 'rgba(220,249,149,0.12)' : 'rgba(255,255,255,0.04)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
                border: '1px solid',
                borderColor: i === selectedIndex ? 'rgba(220,249,149,0.3)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(220,249,149,0.1)',
                  color: NEON,
                },
              }}
            >
              {s}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// PasswordInput
// ============================================================================

const PasswordInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onSubmit: () => void;
}> = ({ value, onChange, placeholder, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
  }, [onSubmit]);

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', px: 3 }}>
      <input
        ref={inputRef}
        type={showPassword ? 'text' : 'password'}
        style={{ ...baseInputStyle, padding: 0, flex: 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Enter password'}
        autoFocus
      />
      <Box
        onClick={() => setShowPassword(!showPassword)}
        sx={{ cursor: 'pointer', color: MUTED, ml: 2, display: 'flex', alignItems: 'center', '&:hover': { color: TEXT_WHITE } }}
      >
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </Box>
    </Box>
  );
};

// ============================================================================
// CodeInput
// ============================================================================

const CodeInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}> = ({ value, onChange, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
  }, [onSubmit]);

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', px: 3, justifyContent: 'center' }}>
      <input
        ref={inputRef}
        style={{ ...baseInputStyle, padding: 0, textAlign: 'center', letterSpacing: '12px', fontSize: '32px', fontWeight: 600, maxWidth: '280px' }}
        value={value}
        onChange={e => {
          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
          onChange(val);
        }}
        onKeyDown={handleKeyDown}
        placeholder="000000"
        maxLength={6}
        autoFocus
        inputMode="numeric"
      />
    </Box>
  );
};

// ============================================================================
// TosInput
// ============================================================================

const TosInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  checkmarks: [boolean, boolean];
  placeholder?: string;
  onSubmit: () => void;
}> = ({ value, onChange, checkmarks, placeholder, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
  }, [onSubmit]);

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', px: 3 }}>
      <input
        ref={inputRef}
        style={{ ...baseInputStyle, padding: 0, flex: 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Type "yes" or "no"'}
        autoFocus
      />
      <Box sx={{ display: 'flex', gap: 1.5, ml: 2, alignItems: 'center', flexShrink: 0 }}>
        <AnimatedCheckmark visible={checkmarks[0]} delay={0} />
        <AnimatedCheckmark visible={checkmarks[1]} delay={400} />
      </Box>
    </Box>
  );
};

// ============================================================================
// TextInput
// ============================================================================

const TextInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputType?: string;
  onSubmit: () => void;
}> = ({ value, onChange, placeholder, inputType, onSubmit }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
  }, [onSubmit]);

  return (
    <input
      ref={inputRef}
      type={inputType || 'text'}
      style={{ ...baseInputStyle, padding: '0 24px' }}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder || ''}
      autoFocus
    />
  );
};

// ============================================================================
// SmartInput (exported)
// ============================================================================

export const SmartInput: React.FC<SmartInputProps> = (props) => {
  const {
    mode,
    value,
    onChange,
    secondaryValue,
    onSecondaryChange,
    suggestions = [],
    onSuggestionSelect,
    checkmarks = [false, false],
    placeholder,
    inputType,
    onSubmit,
  } = props;

  switch (mode) {
    case 'name':
      return (
        <NameInput
          firstName={value}
          onFirstNameChange={onChange}
          lastName={secondaryValue || ''}
          onLastNameChange={onSecondaryChange || (() => {})}
          placeholder={placeholder}
          onSubmit={onSubmit}
        />
      );
    case 'search':
      return (
        <SearchInput
          value={value}
          onChange={onChange}
          suggestions={suggestions}
          onSuggestionSelect={onSuggestionSelect || (() => {})}
          placeholder={placeholder}
          onSubmit={onSubmit}
        />
      );
    case 'password':
      return (
        <PasswordInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onSubmit={onSubmit}
        />
      );
    case 'code':
      return (
        <CodeInput
          value={value}
          onChange={onChange}
          onSubmit={onSubmit}
        />
      );
    case 'tos':
      return (
        <TosInput
          value={value}
          onChange={onChange}
          checkmarks={checkmarks}
          placeholder={placeholder}
          onSubmit={onSubmit}
        />
      );
    case 'text':
    default:
      return (
        <TextInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          inputType={inputType}
          onSubmit={onSubmit}
        />
      );
  }
};
